import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import Logger from 'config/logging';
import moment from 'moment-timezone';
import { notification } from 'antd';

import { djangoDurationToMoment, formatDateTime } from 'library/time';
import { drfErrorsNormalizer } from 'selectors/errors';
import { perfMark } from 'libs/perf/rosterRenderPerf';
import { computePasteTargets } from 'reducers/rosterReducer';

import { requestFailed, requestInFlight, requestSuccess } from './apiActions';
import REDUCER_KEY from '../reducers/reducer.keys';
import RosterApi from '../api/rosterApi';
import TravelApi from '../api/travelApi';
import AvailabilityApi from '../api/availabilityApi';
import CompetenciesApi from '../api/competencyApi';
import { ROSTER_TIMELINE } from './actionTypes';

const types = ROSTER_TIMELINE;

// ========================================
// ROSTER TIMELINE PUBLISH ACTIONS
// ========================================

/**
 * Get the meta information of the Roster version
 * @fires RosterApi#getRosterVersionPublishDetail
 * @param {integer} verionid The version id
 * @param {array} DateRange the date range of the version
 * @return {type} dispatch() Dispatches the action type and response data
 */
export const getPublishMeta = (versionId, DateRange = [null, null]) => {
  const reducerKey = REDUCER_KEY.publishStatus.key;
  return (dispatch) => {
    dispatch(requestInFlight(reducerKey, versionId));
    const from = DateRange[0] === null ? '' : DateRange[0].format('DD-MM-YYYY');
    const to = DateRange[1] === null ? '' : DateRange[1].format('DD-MM-YYYY');
    return RosterApi.getRosterVersionPublishDetail(versionId, from, to)
      .then((response) => {
        dispatch(requestSuccess(response.data.toJS(), reducerKey));
      })
      .catch((error) => {
        const formattedErrors = drfErrorsNormalizer(error.response.data);
        dispatch(requestFailed(formattedErrors, reducerKey));
        throw formattedErrors;
      });
  };
};

/**
 * Publish the Roster from unpublished
 * @fires RosterApi#publishRoster
 * @param {integer} verionid The version id
 * @param {array} DateRange the date range of the Roster
 * @return {type} publishRoster() Updates the status to Published
 */
export const publishRoster = (versionId, DateRange) => () => {
  const from = DateRange[0] === null ? '' : DateRange[0].format('DD-MM-YYYY');
  const to = DateRange[1] === null ? '' : DateRange[1].format('DD-MM-YYYY');
  Logger.info('Publishing Roster');
  return RosterApi.publishRoster(versionId, from, to);
};

// ========================================
// ROSTER TIMELINE ASSIGNMENT ACTIONS
// ========================================

const loadServerAssignmentsSuccess = (rItems, tItems, lItems, aItems) => (dispatch) => {
  // Batch the result dispatches so react-redux coalesces them into a single connected
  // re-render instead of one render per action (SET_SERVER_ITEMS + up to three layer
  // actions). Behaviour and action order are unchanged.
  ReactDOM.unstable_batchedUpdates(() => {
    perfMark('before-SET_SERVER_ITEMS');
    dispatch({
      type: types.SET_SERVER_ITEMS,
      items: rItems
    });
    perfMark('after-SET_SERVER_ITEMS');
    if (tItems) {
      dispatch({
        type: types.SET_TRAVEL_ITEMS,
        items: tItems
      });
    }
    else {
      dispatch({
        type: types.CLEAR_TRAVEL_ITEMS
      });
    }
    if (lItems) {
      dispatch({
        type: types.SET_LEAVE_ITEMS,
        items: lItems
      });
    }
    else {
      dispatch({
        type: types.CLEAR_LEAVE_ITEMS
      });
    }
    if (aItems) {
      dispatch({
        type: types.SET_AVAILABILITY_ITEMS,
        items: aItems
      });
    }
    else {
      dispatch({
        type: types.CLEAR_AVAILABILITY_ITEMS
      });
    }
  });
};

export const swapTimelineAssignmentIds = (ids, items) => ({
  type: types.SWAP_ASSIGNMENTS,
  items,
  ids
});

export const loadServerAssignments = (
  versionId,
  users,
  start,
  end,
  layers,
  includeUnassigned = true,
  afterResultDispatch = null,
  shouldApplyResults = null
) => (dispatch) => {
  const reqs = [RosterApi.getAssignments(versionId, users, start.toISOString(), end.toISOString(), includeUnassigned)];
  const { travel, leave, availability } = layers; // Layers to show

  if (travel) {
    const filters = [
      {
        id: 'itinerary__user__in',
        value: users.join()
      },
      {
        id: 'departure_date__lte',
        value: end.clone()
          .add(1, 'day')
          .toISOString()
      },
      {
        id: 'arrival_date__gte',
        value: start.clone()
          .subtract(1, 'day')
          .toISOString()
      }
    ];
    reqs.push(TravelApi.getAllBookings({
      page: 0,
      pageSize: 5000,
      filters,
      sorted: [{
        id: 'departure_date',
        value: 'ascend'
      }]
    }));
  }
  if (leave) {
    const filters = [
      {
        id: 'user__id__in',
        value: users.join()
      },
      {
        id: 'start_datetime__lte',
        value: end.clone()
          .add(1, 'day')
          .toISOString()
      },
      {
        id: 'end_datetime__gte',
        value: start.clone()
          .subtract(1, 'day')
          .toISOString()
      }
    ];
    reqs.push(AvailabilityApi.getAllAvailabilityLeave({
      page: 0,
      pageSize: 5000,
      filters,
      sorted: []
    }).catch((error) => {
        // Gather all potential error strings from your custom wrapper
        const errorStrings = [
          ...(error?.allErrors || []),
          ...(error?.fieldErrors || []),
          error?.message || ''
        ].map(str => String(str).toLowerCase());
        // Check if any of those strings mention "permission" or "forbidden"
        const isPermissionError = errorStrings.some(str => str.includes('permission') || str.includes('forbidden'));
        // Check for the standard 403 status just in case
        const status = error?.status || error?.response?.status || error?.statusCode;
        const isForbiddenStatus = status === 403;
        if (isPermissionError || isForbiddenStatus) {
            console.warn("You don't have permission to view leave. Safely rendering empty list.");
            // Return empty data structure so the UI renders normally
            return { data: { toJS: () => [] } };
        }
        // Re-throw genuine errors (like 500 crashes) so they are not silently swallowed
        console.error('Unexpected error', error);
        throw error;
      }));
  }
  if (availability) {
    const filters = [
      {
        id: 'user__in',
        value: users.join()
      },
      {
        id: 'start_datetime__lte',
        value: end.clone()
          .add(1, 'day')
          .toISOString()
      },
      {
        id: 'end_datetime__gte',
        value: start.clone()
          .subtract(1, 'day')
          .toISOString()
      }
    ];
    reqs.push(AvailabilityApi.getAllUserCycleLayers({
      page: 0,
      pageSize: 5000,
      filters,
      sorted: []
    }));
  }

  return Promise.all(reqs)
    .then((values) => {
      if (shouldApplyResults && !shouldApplyResults()) {
        return;
      }
      perfMark('promiseAll-then-entry');
      let resolveIndex = 0;
      // The assignment endpoint is bypassed in the axios interceptor (see
      // shouldBypassImmutable), so its data arrives as plain JS — do not toJS() it.
      const rosterItems = values[resolveIndex++].data;
      let travelItems = null;
      let leaveItems = null;
      let availabilityItems = null;
      if (travel) {
        travelItems = values[resolveIndex++].data.toJS();
      }
      if (leave) {
        leaveItems = values[resolveIndex++].data.toJS();
      }
      if (availability) {
        availabilityItems = values[resolveIndex++].data.toJS();
      }
      ReactDOM.unstable_batchedUpdates(() => {
        dispatch(loadServerAssignmentsSuccess(
          rosterItems,
          travelItems,
          leaveItems,
          availabilityItems
        ));
        if (afterResultDispatch) {
          afterResultDispatch();
        }
      });
    });
};

export const saveLocalAssignments = (localItems, deletedItems, travelItems, versionId, start, end) => () => {
  const items = localItems.map(i => ({
    id: i.id,
    activity_id: i.activity.id,
    version_id: versionId,
    notes: i.notes,
    admin_notes: i.admin_notes,
    // OT availability sign-on windows (stored UTC). Undefined is dropped from the JSON,
    // which the server reads as "keep the stored value".
    available_sign_on: i.available_sign_on,
    start: moment(i.start_time),
    end: moment(i.end_time),
    user_id: i.user_id === -1 ? null : i.user_id,
    notifications: i.default_notifications,
    tags: i.tags.map(tag => tag.key)
  }));
  const deletedItemsPositive = _.filter(deletedItems, d => d > 0);
  return RosterApi.createUpdateAssignments(items, deletedItemsPositive, versionId, start, end);
};

export const updateLocalAssignments = items => ({
  type: types.MODIFY_LOCAL_ACTIVITIES,
  items
});

export const updateTravelAssignments = items => ({
  type: types.MODIFY_LOCAL_TRAVEL,
  items
});

export const clearRosterAssignments = () => ({
  type: types.SET_DEFAULT_META
});

export const clearLocalAssignments = () => ({
  type: types.CLEAR_LOCAL_ITEMS
});

export const clearServerAssignments = () => ({
  type: types.CLEAR_SERVER_ITEMS
});

// ========================================
// Competency Validations
// ========================================

export const checkAddedActivities = (checkItems, successCallback) => (dispatch) => {
  const reducerKey = REDUCER_KEY.competencyCheck.key;
  dispatch(requestInFlight(reducerKey));
  return CompetenciesApi.checkCompetenciesValid(checkItems)
    .then(
      (response) => {
        const listData = response.data.toJS();
        if (successCallback !== undefined) {
          successCallback(listData);
        }
        dispatch(requestSuccess(listData, reducerKey));
        return listData;
      },
      (error) => {
        const formattedErrors = drfErrorsNormalizer(error.response.data);
        dispatch(requestFailed(formattedErrors, reducerKey));
        throw formattedErrors;
      }
    );
};

// = =======================================
// ROSTER TIMELINE ACTIVITIES
// = =======================================

export const addTimelineActivity = (activity, group, time, createActivityCount = 1, hoursBetween = 24, maxTimelineDays) => (dispatch) => {
  function notifyInvalidComps(listData) {
    if (listData.length > 0) {
      const e = listData[0]; // Only need the first error, as the user & activity are the same
      const msg = (
        <span>
          <b>{e.user_name}</b>
          {' requires competency '}
          <br />
          <b>{e.name}</b>
          {' for activity '}
          <br />
          <b>{e.activity_name}</b>
        </span>
      );
      notification.warning({
        message: 'Competency Warning',
        description: msg,
        placement: 'bottomLeft'
      });
    }
  }

  dispatch({
    type: types.ADD_ACTIVITY_TO_TIMELINE,
    activity,
    group,
    time,
    createActivityCount,
    hoursBetween,
    maxTimelineDays
  });
  const checkItems = [];
  for (let i = 0; i < createActivityCount; i += 1) {
    const startTime = moment(time)
      .add(i * hoursBetween, 'hours');
    const activityDuration = djangoDurationToMoment(activity.duration);
    const endTime = moment(startTime)
      .add(activityDuration);

    const checkItem = {
      user_id: Number(group.uid),
      activity_id: activity.id,
      check_datetime: formatDateTime(endTime)
    };
    checkItems.push(checkItem);
  }

  dispatch(checkAddedActivities(checkItems, notifyInvalidComps));
};

export const pasteAssignments = (anchorRowId, anchorTime, rosterVersionId) => (dispatch, getState) => {
  function notifyInvalidComps(listData) {
    if (listData.length > 0) {
      const e = listData[0];
      const msg = (
        <span>
          <b>{e.user_name}</b>
          {' requires competency '}
          <br />
          <b>{e.name}</b>
          {' for activity '}
          <br />
          <b>{e.activity_name}</b>
        </span>
      );
      notification.warning({
        message: 'Competency Warning',
        description: msg,
        placement: 'bottomLeft'
      });
    }
  }

  dispatch({
    type: types.PASTE_ASSIGNMENTS,
    anchorRowId,
    anchorTime,
    rosterVersionId
  });

  const { clipboard, user_groups } = getState().rosterTimeline;
  const targets = computePasteTargets(clipboard, user_groups, anchorRowId, anchorTime, rosterVersionId);
  const checkItems = targets
    .filter(t => t.user_id > 0)
    .map(t => ({
      user_id: t.user_id,
      activity_id: t.activity.id,
      check_datetime: formatDateTime(moment(t.end_time))
    }));

  if (checkItems.length) {
    dispatch(checkAddedActivities(checkItems, notifyInvalidComps));
  }
};

export const deleteTimelineActivity = () => ({
  type: types.DELETE_TIMELINE_ACTIVITIES
});

export const selectTimelineActivities = ids => ({
  type: types.SELECT_TIMELINE_ACTIVITIES,
  ids
});

export const copyAssignments = (ids, rosterVersionId) => ({
  type: types.COPY_ASSIGNMENTS,
  ids,
  rosterVersionId
});

export const clearClipboard = () => ({
  type: types.CLEAR_CLIPBOARD
});

// @param {*} changes EG: "{"rowChangeDelta":0,"timeDelta":150}" //min
// @param {*} items EG: "[{"title":"Crew Roster","key":1349,"row":1,"start":"2001-01-03T23:00:00.000Z","end":"2001-01-04T11:00:00.000Z","color":"#4a90e2"},{"title":"Crew Roster","key":1350,"row":1,"start":"2001-01-04T23:00:00.000Z","end":"2001-01-05T11:00:00.000Z","color":"#4a90e2"}]"
// @param {*} maxTimelineDays
//
export const moveTimelineActivities = (changes, items, maxTimelineDays, user_groups) => (dispatch) => {
  function notifyInvalidComps(listData) {
    let shown = 0;
    if (listData.length > 0) {
      const displayedNotices = [];
      for (let i = 0; i < listData.length; i++) {
        const e = listData[i];
        if (displayedNotices.find(n => (n.a_id === e.activity_id && n.u_id === e.user_id))) {
          continue;
        }
        if (++shown > 3) {
          break;
        }
        const msg = (
          <span>
            <b>{e.user_name}</b>
            {' requires competency '}
            <br />
            <b>{e.name}</b>
            {' for activity '}
            <br />
            <b>{e.activity_name}</b>
          </span>
        );
        notification.warning({
          message: 'Competency Warning',
          description: msg,
          placement: 'bottomLeft'
        });
        displayedNotices.push({
          a_id: e.activity_id,
          u_id: e.user_id
        });
      }
    }
    if (shown > 3) {
      const msg = `${listData.length} competency violations - only the first 3 unique ones are shown`;
      notification.error({
        message: 'Competency Warning',
        description: msg,
        placement: 'bottomLeft'
      });
    }
  }

  dispatch({
    type: types.MOVE_ACTIVITIES,
    changes,
    items,
    maxTimelineDays,
    user_groups
  });

  // Check competencies if users change
  if (changes.rowChangeDelta !== 0) {
    const checkItems = items.map((i) => {
      const user = _.find(user_groups, g => g.id === i.row);
      return {
        user_id: user.uid,
        activity_id: i.activity.id,
        check_datetime: i.end.clone()
          .add(changes.timeDelta, 'minutes')
      };
    });
    dispatch(checkAddedActivities(checkItems, notifyInvalidComps));
  }
};

// @param {*} changes EG: "{"isStartTimeChange":false,"timeDelta":0}"
// @param {*} items
// @param {*} maxTimelineDays
//
export const resizeTimelineActivities = (changes, items, maxTimelineDays) => ({
  type: types.RESIZE_ACTIVITIES,
  changes,
  items,
  maxTimelineDays
});

export const setTimelineGroups = (users, activities, hideEmpty = false, hideUnallocatedRows = false) => (dispatch) => {
  // Batch the user/activity group dispatches so they cause one connected re-render.
  ReactDOM.unstable_batchedUpdates(() => {
    if (_.isArray(users)) {
      dispatch({
        type: types.SET_USER_GROUPS,
        users,
        hideEmpty,
        hideUnallocatedRows
      });
    }
    if (_.isArray(activities)) {
      dispatch({
        type: types.SET_ACTIVITY_GROUPS,
        activities,
        hideEmpty
      });
    }
  });
};

export const toggleGroupPin = group => ({
  type: types.TOGGLE_GROUP_PIN,
  group
});

export const addBulkActivitiesToTimeline = activities => ({
  type: types.ADD_BULK_ACTIVITIES,
  activities
});

export const removeActivitiesInAssignmentRange = activities => ({
  type: types.REMOVE_BULK_ACTIVITIES_RANGE,
  activities
});

export const updatePinnedActivities = activities => ({
  type: types.UPDATE_PINNED_ACTIVITIES,
  activities
});

export const updateAssignmentAction = (assignmentId, latest_action, has_unresolved_actions) => ({
  type: types.UPDATE_ASSIGNMENT_ACTION,
  assignmentId,
  latest_action,
  has_unresolved_actions
});
